import { Link, useNavigate } from "react-router-dom";

import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Button,
  Col,
  Form,
  Row,
  Stack,
  Container,
  Card,
} from "react-bootstrap";
import { nameBasedRoutes } from "../../router/public";
import { nameBasedProtectedRoutes } from "../../router/protected";
import DashbordIconCalendar from "../../../src/assets/images/dashbord-icon-calendar.svg";
import DashbordIconPending from "../../../src/assets/images/dashbord-icon-pending.svg";
import DashbordIconCertificates from "../../../src/assets/images/dashbord-icon-certificates.svg";
import DashbordIconDollarSign from "../../../src/assets/images/dashbord-icon-dollar-sign.svg";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import DownloadIocon from "../../../src/assets/images/download-icon.svg";
import DashbordInfoIcon from "../../../src/assets/images/dashbord-info-icon.svg";

import DashboardFileIcon from "../../../src/assets/images/dashboard-file-icon.svg";
import InputSelect from "../../components/common/InputSelect";
import storage from "../../helpers/storage";
import Loader from "../../components/common/Loader";
import "react-datepicker/dist/react-datepicker.css";
import { upcomingInspectionRequest } from "../../services/dashboard";
import InputDateRangePicker from "../../components/common/InputDateRangePicker";
import moment from "moment";
import MaterialReactTable, {
  type MRT_ColumnDef,
  type MRT_SortingState,
} from "material-react-table";
import {
  InspectionTypeDropDown,
  datetimeFormats,
  inspectionStatusGridClassAndLabel,
  inspectionTypes,
  inspectionStatus,
  inspectionStatusWithoutKey,
  inspectionStatusForUpcoming,
} from "../../constants/constants";
import { TopTiles } from "./TopTiles";
import { Expirations } from "./Expirations";
import { DueInvoices } from "./DueInvoices";
import { PendingDeficiences } from "./PendingDeficiences";

/**
 * Create a custom component which will render upcoming inspection list section in dashboard.
 * @param props
 * @returns Upcoming Inspection component
 */
const UpcomingInspectionModule = (props: any) => {
  localStorage.removeItem("inspection_machine_device_list")
  localStorage.removeItem("bill_payment_list")
  const [status, setStatus] = useState("");
  const [sortColumn, setSortColumn] = useState("status");
  const [sortDirection, setSortDirection] = useState("asc");
  const [inspectionType, setInspectionType] = useState("");
  const [inspectionDate, setInspectionDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: sortColumn, desc: false },
  ]); // The default sorting data to show status column sort icon

  useEffect(() => {
    fetchUpcomingInspections({
      sortColumn: 0 in sorting ? sorting[0].id : sortColumn,
      sortDirection:
        0 in sorting
          ? sorting[0].desc === false
            ? "asc"
            : "desc"
          : sortDirection,
      status: status,
      inspectionDate: inspectionDate,
      inspectionType: inspectionType,
    });
  }, [status, inspectionType, inspectionDate, sorting]);

  const fetchUpcomingInspections = (params: any) => {
    setLoading(true);
    upcomingInspectionRequest(
      {
        ...params,
      },
      props.userId
    ).then((response) => {
      setData(response.inspectionResult);
      setLoading(false);
    });
  };

  const inspectionTypeFilter = (event: any) => {
    setInspectionType(event.target.value);
  };

  const statusFilter = (event: any) => {
    setStatus(event.target.value);
  };

  const inspectionDateFilter = (data: any) => {
    const dataRange = data;
    if (dataRange) {
      // since date range picker gives array containing start and end date, we are converting both the dates to YYYY-MM-DD format
      const startDate = moment(data[0]).format(datetimeFormats.databaseFormat);
      const endDate = moment(dataRange[1]).format(
        datetimeFormats.databaseFormat
      );
      setInspectionDate(`${startDate},${endDate}`);
    } else {
      setInspectionDate("");
    }
  };

  /* Column Definition requires for MaterialReactTable */
  type UpcomingInspection = {
    id: string;
    sr_number: string;
    full_address: string;
    no_machines: number;
    inspector: {
      full_name: string;
    };
    inspection_date: string;
    job_time: string;
    status: string;
  };

  // Mentioning table columns belows.
  const tableColumns = useMemo<MRT_ColumnDef<UpcomingInspection>[]>(
    () => [
      {
        accessorKey: "sr_number",
        header: "SR NO.",
        Cell: ({ cell, row }) => (
          <Link
            to={`${nameBasedProtectedRoutes.inspectionDetail.path}/${row.original.id}`}
            className="text-decoration-none text-link"
          >
            <strong>{cell.getValue<string>()}</strong>
          </Link>
        ),
      },
      {
        accessorKey: "full_address",
        header: "LOCATION",
      },
      {
        accessorKey: "no_machines",
        header: "NO. OF MACHINES",
      },
      {
        accessorKey: "inspector.full_name",
        header: "INSPECTOR",
        Cell: ({ cell, row }) => {
          if (row.original.inspector) {
            return cell.getValue<string>();
          } else {
            return "-";
          }
        },
      },
      {
        accessorKey: "inspection_date",
        header: "INSPECTION DATE & TIME",
        Cell: ({ cell, row }) => {
          return row.original.status !== inspectionStatusWithoutKey.Pending
            ? moment(
                `${row.original.inspection_date} ${row.original.job_time}`
              ).format(datetimeFormats.separatedDateTime)
            : "-";
        },
      },
      {
        accessorKey: "status",
        header: "STATUS",
        Cell: ({ cell }) => (
          /** Since, status columns showing status in different background and font colors, we are applying dynamic classes here based on the keys */
          <span
            className={`${
              inspectionStatusGridClassAndLabel[
                cell.getValue() as keyof typeof inspectionStatusGridClassAndLabel
              ]["class"]
            } text-center br-5 p-1 pe-2 ps-2 fs-15 d-inline-block`}
          >
            {
              inspectionStatusGridClassAndLabel[
                cell.getValue<string>() as keyof typeof inspectionStatusGridClassAndLabel
              ]["label"]
            }
          </span>
        ),
      },
    ],
    []
  );

  return (
    <Row className="mb-4" id="upcomingInspection">
      <Col md={12}>
        <Card className="border-0">
          <Card.Header className="border-0 bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11">
            UPCOMING INSPECTIONS
          </Card.Header>
          <Card.Body className="pe-4 ps-4 pt-4">
            <Row className="gx-3">
              <Col className="dashboard-input-cover">
                <InputSelect
                  controlId="inspection_type"
                  label="Inspection Type"
                  placeholder="Inspection Type"
                  touched={false}
                  handleBlur={false}
                  handleChange={inspectionTypeFilter}
                  errorsField={false}
                  value={""}
                  options={[
                    { key: "all", value: "All Types" },
                    ...inspectionTypes,
                  ]}
                />
              </Col>
              <Col className="inspector-date-icon inspector-start-date dashboard-input-cover">
                <InputDateRangePicker
                  controlId="inspection_date"
                  label="Inspection Date"
                  placeholder="Date Range"
                  touched={false}
                  handleBlur={false}
                  handleChange={inspectionDateFilter}
                  handleClose={inspectionDateFilter}
                  errorsField={false}
                  value={false}
                />
              </Col>
              <Col className="dashboard-input-cover">
                <InputSelect
                  controlId="status"
                  label="Status"
                  placeholder="Status"
                  touched={false}
                  handleBlur={false}
                  handleChange={statusFilter}
                  errorsField={false}
                  value={""}
                  options={[
                    { key: "all", value: "All Statuses" },
                    ...inspectionStatusForUpcoming,
                  ]}
                />
              </Col>
            </Row>

            <div className="table-responsive dashboard-table-data">
              <MaterialReactTable
                muiTableProps={{
                  classes: {
                    root: "table custom-table border-none-table table-striped dashboard-table",
                  },
                }}
                enableGlobalFilter={false}
                enableFullScreenToggle={false}
                enableDensityToggle={false}
                enableHiding={false}
                enableColumnFilters={false}
                enablePagination={false}
                onSortingChange={setSorting}
                manualSorting
                columns={tableColumns}
                data={data}
                state={{ sorting }}
              />
            </div>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const inspectionRequestPage = () => {
    navigate(nameBasedProtectedRoutes.newInspectionRequest.path);
  };
  const [loading, setLoading] = useState(true);

  const userInformation: any = storage.getData("user-info");

  const userId = userInformation.id;

  return (
    <div className="not-found">
      <section className="profile-main pt-4 dashboard-content">
        <Container className="">
          <Row className="mb-4">
            <Col md={6} lg={6}>
              <h1 className="fs-30 fw-bold mb-3 mt-2 black-color">Dashboard</h1>
            </Col>
            <Col
              md={6}
              lg={6}
              className="align-items-start col-lg-6 col-md-6 d-flex justify-content-md-end mb-lg-0 mb-3"
            >
              <Button
                className="fs-15 br-50 fw-semibold text-black"
                variant="primary"
                onClick={inspectionRequestPage}
              >
                <img src={DashboardFileIcon} className="me-2" />
                New Inspection Request
              </Button>
            </Col>
          </Row>

          <TopTiles userId={userId} />

          <UpcomingInspectionModule userId={userId} />
          <PendingDeficiences userId={userId} />
          <Expirations userId={userId} />
          <DueInvoices userId={userId} />
         

          {/* <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0">
                <Card.Header className="bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11 border-0">
                  EXPIRATIONS (UPCOMING/EXPIRED)
                </Card.Header>
                <Card.Body className="pe-4 ps-4 pt-4">
                  <div className="table-responsive">
                    <table className="table custom-table border-none-table table-striped">
                      <thead>
                        <tr>
                          <th>DEVICE ID</th>
                          <th>MODEL NO.</th>
                          <th>SERIAL NO.</th>
                          <th>LOCATION</th>
                          <th>INSPECTION DATE</th>
                          <th>STATUS</th>
                          <th>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            123548{" "}
                            <OverlayTrigger
                              overlay={<Tooltip>Expired in: 9 days</Tooltip>}
                            >
                              <span className="d-inline-block vt ms-2">
                                <img src={DashbordInfoIcon} className="vt-t" />
                              </span>
                            </OverlayTrigger>{" "}
                          </td>
                          <td>MLC150-1</td>
                          <td>2212503</td>
                          <td>1807 Harding Dr, Parsons, KS 67357, USA </td>

                          <td>12-24-2022</td>

                          <td>
                            <span className="text-center bg-light-purple text-purple br-5 p-1 pe-2 ps-2  fs-15 d-inline-block">
                              In Progress
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-inspection-req">
                              Submit Inspection Request
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            123548{" "}
                            <OverlayTrigger
                              overlay={<Tooltip>Expired in: 9 days</Tooltip>}
                            >
                              <span className="d-inline-block vt ms-2">
                                <img src={DashbordInfoIcon} className="vt-t" />
                              </span>
                            </OverlayTrigger>{" "}
                          </td>
                          <td>MLC150-1</td>
                          <td>2212503</td>
                          <td>1807 Harding Dr, Parsons, KS 67357, USA </td>

                          <td>12-24-2022</td>

                          <td>
                            <span className="text-center bg-light-red text-red br-5 p-1 pe-2 ps-2  fs-15 d-inline-block">
                              Expired
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-inspection-req">
                              Submit Inspection Request
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            123548{" "}
                            <OverlayTrigger
                              overlay={<Tooltip>Expired in: 9 days</Tooltip>}
                            >
                              <span className="d-inline-block vt ms-2">
                                <img src={DashbordInfoIcon} className="vt-t" />
                              </span>
                            </OverlayTrigger>{" "}
                          </td>
                          <td>MLC150-1</td>
                          <td>2212503</td>
                          <td>1807 Harding Dr, Parsons, KS 67357, USA </td>

                          <td>12-24-2022</td>

                          <td>
                            <span className="text-center bg-ligt-green text-green br-5 p-1 pe-2 ps-2  fs-15 d-inline-block">
                              Completed
                            </span>
                          </td>
                          <td>
                            <button className="btn btn-inspection-req">
                              Submit Inspection Request
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0">
                <Card.Header className="bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11 border-0">
                  INVOICES DUE
                </Card.Header>
                <Card.Body className="pe-4 ps-4 pt-4">
                  <div className="table-responsive">
                    <table className="table custom-table border-none-table table-striped">
                      <thead>
                        <tr>
                          <th>SR NO.</th>
                          <th>LOCATION</th>
                          <th>AMOUNT</th>

                          <th>INSPECTION DATE</th>
                          <th>DAYS PAST DUE</th>
                          <th>ACTION</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <a
                              href=""
                              className="text-decoration-none text-link"
                            >
                              SR-2212503
                            </a>
                          </td>
                          <td>1807 Harding Dr, Parsons, KS 67357, USA</td>
                          <td>$250.00</td>
                          <td>12-24-2022 | 4:05 AM </td>
                          <td>12</td>
                          <td>
                            <a href="" className="ms-2">
                              <img src={DownloadIocon} />
                            </a>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <a
                              href=""
                              className="text-decoration-none text-link"
                            >
                              SR-2212503
                            </a>
                          </td>
                          <td>1807 Harding Dr, Parsons, KS 67357, USA</td>
                          <td>$250.00</td>
                          <td>12-24-2022 | 4:05 AM </td>
                          <td>12</td>
                          <td>
                            <a href="" className="ms-2">
                              <img src={DownloadIocon} />
                            </a>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <a
                              href=""
                              className="text-decoration-none text-link"
                            >
                              SR-2212503
                            </a>
                          </td>
                          <td>1807 Harding Dr, Parsons, KS 67357, USA</td>
                          <td>$250.00</td>
                          <td>12-24-2022 | 4:05 AM </td>
                          <td>12</td>
                          <td>
                            <a href="" className="ms-2">
                              <img src={DownloadIocon} />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0">
                <Card.Header className="bg-default bg-theme black-color fs-20 fw-bold ls-2 btlr-14 btrr-14 pe-4 ps-4 pt-11 border-0">
                  PENDING DEFICIENCIES
                </Card.Header>
                <Card.Body className="pe-4 ps-4 pt-4">
                  <div className="table-responsive">
                    <table className="table custom-table border-none-table table-striped">
                      <thead>
                        <tr>
                          <th>DEVICE ID</th>
                          <th>MODEL NO.</th>
                          <th>SERIAL NO.</th>
                          <th>LOCATION</th>
                          <th>INSPECTION DATE</th>
                          <th>DAYS SINCE ISSUED</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>123548</td>
                          <td>MLC150-1</td>
                          <td>2212503</td>
                          <td>
                            1807 Harding Dr, Parsons, KS 67357, United States{" "}
                          </td>
                          <td>12-24-2022</td>
                          <td>23</td>
                        </tr>
                        <tr>
                          <td>123548</td>
                          <td>MLC150-1</td>
                          <td>2212503</td>
                          <td>
                            1807 Harding Dr, Parsons, KS 67357, United States{" "}
                          </td>
                          <td>12-24-2022</td>
                          <td>23</td>
                        </tr>
                        <tr>
                          <td>123548</td>
                          <td>MLC150-1</td>
                          <td>2212503</td>
                          <td>
                            1807 Harding Dr, Parsons, KS 67357, United States{" "}
                          </td>
                          <td>12-24-2022</td>
                          <td>23</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row> */}
        </Container>
      </section>
    </div>
  );
};
